import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiX,
  FiUploadCloud,
  FiFile,
  FiCheck,
  FiAlertCircle,
} from "react-icons/fi";
import {
  validateFileBeforeUpload,
  formatFileSize,
  getFileCategory,
} from "../../../utils/fileUtils";
import "./FileUploadModal.css";

const FileUploadModal = ({ roomId, onClose, onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    tags: "",
    category: "",
  });
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const validatedFiles = files.map((file) => {
      const validation = validateFileBeforeUpload(file);
      return {
        file,
        id: Math.random().toString(36).substr(2, 9),
        validation,
        progress: 0,
      };
    });
    setSelectedFiles((prev) => [...prev, ...validatedFiles]);
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleUpload = async () => {
    const validFiles = selectedFiles.filter((f) => f.validation.valid);
    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = validFiles.map(async (fileObj) => {
        // Map the detected file type to a valid backend category
        const autoCategory = getFileCategory(fileObj.validation.fileType);
        const options = {
          description: formData.description,
          tags: formData.tags.split(",").map((t) => t.trim()).filter(Boolean),
          category: formData.category || autoCategory,
        };

        const onProgress = (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [fileObj.id]: progress,
          }));
        };

        return onUploadSuccess(fileObj.file, options, onProgress);
      });

      await Promise.all(uploadPromises);
      onClose();
    } catch (error) {
alert("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const validCount = selectedFiles.filter((f) => f.validation.valid).length;

  return (
    <motion.div
      className="rfum-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="rfum-modal"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rfum-header">
          <div className="rfum-header-title">
            <div className="rfum-header-icon">
              <FiUploadCloud size={22} />
            </div>
            <h2>Upload Files</h2>
          </div>
          <button className="rfum-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="rfum-content">
          {/* Drop Zone */}
          <div
            className={`rfum-dropzone ${dragActive ? "rfum-dropzone--active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="rfum-dropzone-icon">
              <FiUploadCloud size={40} />
            </div>
            <p className="rfum-dropzone-text">
              Drag & drop files here or <span>browse</span>
            </p>
            <span className="rfum-dropzone-hint">
              Max size varies by file type (up to 500MB for videos)
            </span>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInput}
              style={{ display: "none" }}
            />
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="rfum-files">
              <h3 className="rfum-files-title">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="rfum-files-list">
                {selectedFiles.map((fileObj) => (
                  <div
                    key={fileObj.id}
                    className={`rfum-file ${!fileObj.validation.valid ? "rfum-file--invalid" : ""}`}
                  >
                    <div className="rfum-file-icon">
                      {fileObj.validation.valid ? (
                        <FiFile size={20} />
                      ) : (
                        <FiAlertCircle size={20} />
                      )}
                    </div>
                    <div className="rfum-file-info">
                      <span className="rfum-file-name">{fileObj.file.name}</span>
                      <span className="rfum-file-size">
                        {formatFileSize(fileObj.file.size)}
                      </span>
                      {!fileObj.validation.valid && (
                        <span className="rfum-file-error">
                          {fileObj.validation.error}
                        </span>
                      )}
                    </div>

                    {uploading && uploadProgress[fileObj.id] !== undefined ? (
                      <div className="rfum-file-progress">
                        <div
                          className="rfum-file-progress-bar"
                          style={{ width: `${uploadProgress[fileObj.id]}%` }}
                        />
                        <span>{uploadProgress[fileObj.id]}%</span>
                      </div>
                    ) : (
                      <button
                        className="rfum-file-remove"
                        onClick={() => removeFile(fileObj.id)}
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata Form */}
          {selectedFiles.length > 0 && (
            <div className="rfum-form">
              <h3 className="rfum-form-title">File Details (Optional)</h3>

              <div className="rfum-field">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Add a description..."
                  rows={2}
                  disabled={uploading}
                />
              </div>

              <div className="rfum-field-row">
                <div className="rfum-field">
                  <label>Tags</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData({ ...formData, tags: e.target.value })
                    }
                    placeholder="e.g., dataset, final"
                    disabled={uploading}
                  />
                </div>

                <div className="rfum-field">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    disabled={uploading}
                  >
                    <option value="">Auto-detect</option>
                    <option value="papers">Papers</option>
                    <option value="data">Data</option>
                    <option value="code">Code</option>
                    <option value="images">Images</option>
                    <option value="documents">Documents</option>
                    <option value="presentations">Presentations</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rfum-footer">
          <button className="rfum-btn rfum-btn--secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </button>
          <button
            className="rfum-btn rfum-btn--primary"
            onClick={handleUpload}
            disabled={uploading || validCount === 0}
          >
            {uploading ? (
              <>
                <div className="rfum-spinner"></div>
                Uploading...
              </>
            ) : (
              <>
                <FiUploadCloud size={18} />
                Upload {validCount > 0 ? `${validCount} File${validCount > 1 ? "s" : ""}` : ""}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FileUploadModal;
